/**
 * AI Content Detection Service
 * 
 * Provides client-side interface for detecting AI-generated content in documents.
 * Calls the backend hybrid detection API that combines LLM analysis with stylometric features.
 */

import fetchService from '../../../../services/fetchService';
import { API_BASE_URL } from '../../../../api/config';
import { createFileLogger } from '../../../../utils/logger';

const log = createFileLogger('AI_DETECTION_SERVICE');

export interface SuspiciousSpan {
  start: number;
  end: number;
  reason: string;
  excerpt?: string;
  match_method?: string;
}

export interface AIDetectionResult {
  run_id: string;
  ai_likelihood: number;
  confidence: 'low' | 'medium' | 'high';
  label: string;
  explanations: string[];
  suspicious_spans: SuspiciousSpan[];
  features: Record<string, number>;
  debug?: {
    llm_score: number;
    stylometric_score: number;
  };
}

export interface AIDetectionRunSummary {
  run_id: string;
  timestamp: string;
  ai_likelihood: number;
  label: string;
  confidence: string;
  text_length: number;
  suspicious_spans_count: number;
  result: AIDetectionResult;
  entity_id?: string;
  entity_name?: string;
}

/**
 * Detect AI-generated content in a document
 * 
 * @param docId - Document identifier
 * @param text - Full document text to analyze
 * @param language - Document language (default: 'en')
 * @returns Detection result with scores, explanations, and suspicious spans
 */
export async function detectAIContent(
  docId: string,
  text: string,
  language: string = 'en'
): Promise<AIDetectionResult> {
  try {
    log.log('[detectAIContent] Starting detection', { docId, textLength: text.length });

    if (!text || text.trim().length < 10) {
      throw new Error('Text must be at least 10 characters long');
    }

    const response = await fetchService(`${API_BASE_URL}/docs/detect-ai-content`, {
      method: 'POST',
      body: JSON.stringify({
        doc_id: docId,
        text,
        language
      })
    });

    if (!response) {
      throw new Error('No response from AI detection service');
    }

    log.log('[detectAIContent] Detection complete', {
      docId,
      label: response.label,
      likelihood: response.ai_likelihood,
      confidence: response.confidence
    });

    // Usage tracking is handled automatically by backend via @billing_tracker decorator
    // No need for separate credit tracking - AI Detection now uses usage-based billing with 2x markup

    return response as AIDetectionResult;
  } catch (error: any) {
    log.error('[detectAIContent] Detection failed', error);
    
    // Re-throw with more context
    if (error.message?.includes('429')) {
      throw new Error('Usage limit reached. Please upgrade your plan to continue.');
    } else if (error.message?.includes('401')) {
      throw new Error('Authentication required. Please log in.');
    } else {
      throw new Error(error.message || 'AI detection failed. Please try again.');
    }
  }
}

/**
 * Check health status of AI detection service
 * 
 * @returns Health status and available features
 */
export async function checkDetectionServiceHealth(): Promise<{
  status: string;
  dependencies: Record<string, boolean>;
  features_available: Record<string, boolean>;
}> {
  try {
    const response = await fetchService(`${API_BASE_URL}/docs/detect-ai-content/health`, {
      method: 'GET'
    });

    return response;
  } catch (error) {
    log.error('[checkDetectionServiceHealth] Health check failed', error);
    return {
      status: 'unknown',
      dependencies: {},
      features_available: {}
    };
  }
}

/**
 * Format confidence level for display
 */
export function formatConfidence(confidence: string): string {
  switch (confidence) {
    case 'high':
      return 'High Confidence';
    case 'medium':
      return 'Medium Confidence';
    case 'low':
      return 'Low Confidence';
    default:
      return confidence;
  }
}

/**
 * Get color for confidence level
 */
export function getConfidenceColor(confidence: string): string {
  switch (confidence) {
    case 'high':
      return '#d32f2f'; // Red
    case 'medium':
      return '#f57c00'; // Orange
    case 'low':
      return '#fbc02d'; // Yellow
    default:
      return '#9e9e9e'; // Gray
  }
}

/**
 * Format AI likelihood score as percentage
 */
export function formatLikelihoodPercentage(likelihood: number): string {
  return `${Math.round(likelihood * 100)}%`;
}

/**
 * Get badge style based on label
 */
export function getLabelStyle(label: string): { color: string; backgroundColor: string } {
  if (label.includes('Very Likely AI')) {
    return { color: '#fff', backgroundColor: '#d32f2f' };
  } else if (label.includes('Likely AI')) {
    return { color: '#fff', backgroundColor: '#f57c00' };
  } else if (label.includes('Uncertain')) {
    return { color: '#000', backgroundColor: '#fdd835' };
  } else {
    return { color: '#fff', backgroundColor: '#388e3c' }; // Human - green
  }
}

/**
 * Track AI detection usage for billing
 * 
 * @param docId - Document identifier
 * @param metadata - Usage metadata (text length, result)
 */
export async function trackAIDetectionUsage(
  docId: string,
  metadata: { textLength: number; result: AIDetectionResult }
): Promise<void> {
  try {
    await fetchService(`${API_BASE_URL}/billing/ai-detection-usage`, {
      method: 'POST',
      body: JSON.stringify({
        doc_id: docId,
        text_length: metadata.textLength,
        ai_likelihood: metadata.result.ai_likelihood,
        timestamp: new Date().toISOString()
      })
    });
    
    log.log('[trackAIDetectionUsage] Usage tracked successfully', { docId });
  } catch (error) {
    log.error('[trackAIDetectionUsage] Failed to track usage', error);
    // Don't throw - tracking failure shouldn't break detection
  }
}

/**
 * Get AI detection credits remaining for current user
 * 
 * @returns Credits information (remaining, total, used)
 */
export async function getAIDetectionCredits(): Promise<{
  remaining: number;
  total: number;
  used: number;
}> {
  try {
    const response = await fetchService(`${API_BASE_URL}/billing/ai-detection-credits`, {
      method: 'GET'
    });
    
    return {
      remaining: response.remaining ?? 0,
      total: response.total ?? 0,
      used: response.used ?? 0
    };
  } catch (error) {
    log.error('[getAIDetectionCredits] Failed to fetch credits', error);
    // Return zeros on error to avoid breaking UI
    return { remaining: 0, total: 0, used: 0 };
  }
}

/**
 * Get AI detection run history for a document
 * 
 * @param docId - Document identifier
 * @returns List of detection runs sorted by most recent first
 */
export async function getAIDetectionRuns(docId: string): Promise<AIDetectionRunSummary[]> {
  try {
    const response = await fetchService(`${API_BASE_URL}/docs/ai-detection-runs/${docId}`, {
      method: 'GET'
    });
    
    return response.runs || [];
  } catch (error) {
    log.error('[getAIDetectionRuns] Failed to fetch detection runs', error);
    return [];
  }
}

/**
 * Get ALL AI detection run history across all documents
 * 
 * @returns List of detection runs sorted by most recent first with entity information
 */
export async function getAllAIDetectionRuns(): Promise<AIDetectionRunSummary[]> {
  try {
    const response = await fetchService(`${API_BASE_URL}/docs/ai-detection-runs`, {
      method: 'GET'
    });
    
    return response.runs || [];
  } catch (error) {
    log.error('[getAllAIDetectionRuns] Failed to fetch all detection runs', error);
    return [];
  }
}

